// src/pages/AboutMe.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../components/AppNav';
import FormattedText from '../components/FormattedText';
import { fetchSiteContent } from '../services/firebase';

function AboutMe({ demoMode, previewContent }) {
    const navigate = useNavigate();
    const [c, setC] = useState(previewContent || null);
    const [loading, setLoading] = useState(!previewContent);

    useEffect(() => {
        // Admin live-preview: render from the draft content + skip the fetch.
        // Without previewContent (every real visitor) this is byte-identical.
        if (previewContent) { setC(previewContent); setLoading(false); return; }
        fetchSiteContent().then((content) => {
            setC(content || {});
            setLoading(false);
        });
    }, [previewContent]);

    const cardStyle = {
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfd 40%, #f6f8fa 100%)',
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.5)',
        boxShadow: 'var(--shadow-md)',
        position: 'relative',
        overflow: 'hidden',
    };

    const specHighlight = {
        position: 'absolute', top: 0, left: 0, right: 0, height: '50%',
        background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.14) 0%, transparent 60%)',
        pointerEvents: 'none', zIndex: 0, borderRadius: '24px 24px 0 0',
    };

    if (loading) {
        return (
            <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
                <AppNav demoMode={demoMode} />
                <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px' }}>
                    <div className="mom-skeleton" style={{ width: 140, height: 140, borderRadius: '50%', margin: '0 auto 24px' }} />
                    <div className="mom-skeleton" style={{ height: 36, borderRadius: 14, width: '60%', margin: '0 auto 16px' }} />
                    <div className="mom-skeleton" style={{ height: 200, borderRadius: 20 }} />
                </div>
            </div>
        );
    }

    const hasPhoto = c.aboutPhoto && c.aboutPhoto.trim();
    const headline = c.aboutHeadline || 'Hey, I\u2019m Danielle!';
    const tagline = c.aboutTagline || '';
    const intro = c.aboutIntro || '';
    const body = c.aboutBody || '';
    const closingHeadline = c.aboutClosingHeadline || '';
    const closing = c.aboutClosing || '';
    const ctaText = c.aboutCtaText || 'Browse Programs';
    const ctaLink = c.aboutCtaLink || '/programs';

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px 80px' }}>

                {/* ── Hero section ─────────────────────────────── */}
                <div className="slide-up" style={{ textAlign: 'center', marginBottom: 36 }}>
                    {/* Photo */}
                    {hasPhoto ? (
                        <div style={{
                            width: 160, height: 160, borderRadius: '50%', margin: '0 auto 28px',
                            overflow: 'hidden',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1), 0 0 0 4px rgba(255,255,255,0.7), 0 0 0 6px color-mix(in srgb, var(--mom-primary) 15%, transparent)',
                        }}>
                            <img src={c.aboutPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                        </div>
                    ) : (
                        <div style={{
                            width: 120, height: 120, borderRadius: '50%', margin: '0 auto 28px',
                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 8px 32px color-mix(in srgb, var(--mom-primary) 25%, transparent)',
                        }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 48, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>D</span>
                        </div>
                    )}

                    <h1 style={{
                        fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 5vw, 38px)',
                        fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)',
                        lineHeight: 1.2, marginBottom: tagline ? 10 : 0,
                    }}>
                        {headline}
                    </h1>
                    {tagline && (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 16,
                            color: 'var(--mom-primary)', fontWeight: 600, lineHeight: 1.5,
                        }}>
                            {tagline}
                        </p>
                    )}
                </div>

                {/* ── Intro section ────────────────────────────── */}
                {intro && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '36px 32px', marginBottom: 20, animationDelay: '0.08s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <FormattedText text={intro} style={{
                                fontFamily: 'var(--mom-font-serif)', fontSize: 18,
                                fontStyle: 'italic', color: 'var(--mom-charcoal)',
                                lineHeight: 1.7, textAlign: 'center',
                            }} />
                        </div>
                    </div>
                )}

                {/* ── Main body ─────────────────────────────────── */}
                {body && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '36px 32px', marginBottom: 20, animationDelay: '0.16s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <FormattedText text={body} style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 15,
                                color: 'var(--mom-charcoal)', lineHeight: 1.8,
                            }} />
                        </div>
                    </div>
                )}

                {/* ── Closing section ──────────────────────────── */}
                {(closingHeadline || closing) && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '36px 32px', marginBottom: 20, animationDelay: '0.24s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                            {closingHeadline && (
                                <h2 style={{
                                    fontFamily: 'var(--mom-font-serif)', fontSize: 24,
                                    fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)',
                                    marginBottom: 12,
                                }}>
                                    {closingHeadline}
                                </h2>
                            )}
                            {closing && (
                                <FormattedText text={closing} style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 15,
                                    color: 'var(--mom-warm-gray)', lineHeight: 1.8,
                                }} />
                            )}
                        </div>
                    </div>
                )}

                {/* ── CTA ──────────────────────────────────────── */}
                {ctaText && (
                    <div className="slide-up" style={{ textAlign: 'center', marginTop: 32, animationDelay: '0.32s' }}>
                        <button className="mom-btn mom-btn-primary" onClick={() => {
                            if (ctaLink.startsWith('/')) navigate(ctaLink);
                            else window.open(ctaLink, '_blank');
                        }} style={{ padding: '16px 44px', fontSize: 16, borderRadius: 50 }}>
                            {ctaText} →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AboutMe;
